import { useState, useCallback } from 'react';
import { Check, FlaskConical, Calculator, Trophy, AlertCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { copperCycleColors } from '@/config/experiments/chem1131/experiment5.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Entering Initial Mass',
    content: 'The initial mass is the mass of copper wire you started with at the beginning of the experiment. This value should have been recorded in Part 1 when you weighed the cleaned copper wire.',
    whatHappens: 'You enter the initial mass of copper that was recorded at the start of the experiment. This is your theoretical yield - the maximum amount of copper you could possibly recover.',
    observe: [
      'Initial mass should be approximately 0.5 g',
      'Use the value recorded in your lab notebook',
      'This represents 100% of available copper atoms',
      'Mass should be recorded to 4 decimal places'
    ]
  },
  2: {
    title: 'Entering Final Mass',
    content: 'The final mass is the mass of dried copper recovered at the end of the experiment. This value was calculated by subtracting the dish and boiling chips masses from the total mass.',
    whatHappens: 'You enter the final mass of recovered copper. This is your experimental (actual) yield - the amount of copper you actually obtained.',
    observe: [
      'Final mass from Part 9 weighing',
      'Should be less than or equal to initial mass',
      'If greater, check for contamination or incomplete drying',
      'This is your experimental yield'
    ]
  },
  3: {
    title: 'Calculating Percent Yield',
    content: 'Percent yield measures how much product you recovered compared to the theoretical maximum:\n\n% Yield = (Experimental Yield / Theoretical Yield) x 100\n\nIn the copper cycle, theoretical yield equals initial mass because copper atoms are conserved.',
    whatHappens: 'The percent yield is calculated by dividing the final mass by the initial mass and multiplying by 100. A yield of 85-100% indicates good technique.',
    observe: [
      '90-100%: Excellent technique',
      '85-90%: Good technique',
      '75-85%: Acceptable, some losses',
      '<75%: Review transfer techniques',
      '>100%: Contamination or incomplete drying'
    ]
  }
};

export default function Part10Yield() {
  const [initialMassEntered, setInitialMassEntered] = useState(false);
  const [finalMassEntered, setFinalMassEntered] = useState(false);
  const [yieldCalculated, setYieldCalculated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Simulated data - in real app, these would come from previous parts
  const initialMass = 0.5000;
  const finalMass = 0.4350;
  const percentYield = (finalMass / initialMass) * 100;

  const completedSteps = [initialMassEntered, finalMassEntered, yieldCalculated].filter(Boolean).length;

  const handleEnterInitial = useCallback(() => {
    if (!initialMassEntered) {
      setInitialMassEntered(true);
      setCurrentStep(2);
    }
  }, [initialMassEntered]);

  const handleEnterFinal = useCallback(() => {
    if (initialMassEntered && !finalMassEntered) {
      setFinalMassEntered(true);
      setCurrentStep(3);
    }
  }, [initialMassEntered, finalMassEntered]);

  const handleCalculate = useCallback(() => {
    if (finalMassEntered && !yieldCalculated) {
      setYieldCalculated(true);
    }
  }, [finalMassEntered, yieldCalculated]);

  const getYieldStatus = () => {
    if (percentYield > 100) return { icon: AlertCircle, color: '#EF4444', text: 'Over 100% - Check for contamination!' };
    if (percentYield >= 90) return { icon: Trophy, color: '#FFD700', text: 'Excellent technique!' };
    if (percentYield >= 85) return { icon: Check, color: '#4ADE80', text: 'Good yield' };
    if (percentYield >= 75) return { icon: Check, color: '#60A5FA', text: 'Acceptable yield' };
    return { icon: AlertCircle, color: '#FBBF24', text: 'Low yield - check transfers' };
  };

  const yieldStatus = getYieldStatus();

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part X: Calculate Yield</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Initial Mass', hint: 'Enter starting copper mass', done: initialMassEntered },
            { id: 2, title: 'Final Mass', hint: 'Enter recovered copper mass', done: finalMassEntered },
            { id: 3, title: 'Calculate Yield', hint: 'Compute percent yield', done: yieldCalculated }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <LabBench
        experimentName="Percent Yield Calculation"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Calculator', 'Lab notebook']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="calculation-workspace">
          {/* Data Entry Cards */}
          <div className="data-cards">
            {/* Initial Mass Card */}
            <div
              className={`data-card ${currentStep === 1 && !initialMassEntered ? 'active clickable' : ''}`}
              onClick={handleEnterInitial}
            >
              <div className="card-header">
                <span className="card-label">Initial Mass</span>
                {initialMassEntered && <Check size={16} className="check-icon" />}
              </div>
              <div className="card-value">
                {initialMassEntered ? (
                  <span className="value">{initialMass.toFixed(4)} g</span>
                ) : (
                  <span className="placeholder">Click to enter</span>
                )}
              </div>
              <div className="card-desc">Starting copper wire</div>
            </div>

            {/* Arrow */}
            <div className="arrow-container">
              <svg viewBox="0 0 40 20" width={40} height={20}>
                <path d="M0 10 L30 10 M25 5 L30 10 L25 15" fill="none" stroke="#555" strokeWidth="2" />
              </svg>
            </div>

            {/* Final Mass Card */}
            <div
              className={`data-card ${currentStep === 2 && !finalMassEntered ? 'active clickable' : ''}`}
              onClick={handleEnterFinal}
            >
              <div className="card-header">
                <span className="card-label">Final Mass</span>
                {finalMassEntered && <Check size={16} className="check-icon" />}
              </div>
              <div className="card-value">
                {finalMassEntered ? (
                  <span className="value">{finalMass.toFixed(4)} g</span>
                ) : (
                  <span className="placeholder">Click to enter</span>
                )}
              </div>
              <div className="card-desc">Recovered copper</div>
            </div>

            {/* Arrow */}
            <div className="arrow-container">
              <svg viewBox="0 0 40 20" width={40} height={20}>
                <path d="M0 10 L30 10 M25 5 L30 10 L25 15" fill="none" stroke="#555" strokeWidth="2" />
              </svg>
            </div>

            {/* Yield Card */}
            <div
              className={`data-card yield-card ${currentStep === 3 && !yieldCalculated ? 'active clickable' : ''} ${yieldCalculated ? 'calculated' : ''}`}
              onClick={handleCalculate}
            >
              <div className="card-header">
                <span className="card-label">Percent Yield</span>
                {yieldCalculated && <yieldStatus.icon size={16} style={{ color: yieldStatus.color }} />}
              </div>
              <div className="card-value">
                {yieldCalculated ? (
                  <span className="value yield" style={{ color: yieldStatus.color }}>
                    {percentYield.toFixed(1)}%
                  </span>
                ) : (
                  <span className="placeholder">Click to calculate</span>
                )}
              </div>
              {yieldCalculated && (
                <div className="card-desc status" style={{ color: yieldStatus.color }}>
                  {yieldStatus.text}
                </div>
              )}
            </div>
          </div>

          {/* Calculation Box */}
          <div className="calculation-box">
            <h4><Calculator size={16} /> Yield Calculation</h4>
            <div className="formula">
              <div className="formula-line">
                % Yield = (Experimental Yield / Theoretical Yield) x 100
              </div>
              {finalMassEntered && (
                <>
                  <div className="formula-line">
                    % Yield = ({finalMass.toFixed(4)} g / {initialMass.toFixed(4)} g) x 100
                  </div>
                  {yieldCalculated && (
                    <div className="formula-line result">
                      % Yield = <span style={{ color: yieldStatus.color }}>{percentYield.toFixed(1)}%</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {!yieldCalculated && currentStep === 1 && (
              <div className="status-message info">
                <Calculator size={20} />
                Enter your initial copper mass to begin
              </div>
            )}
            {!yieldCalculated && currentStep === 2 && (
              <div className="status-message info">
                <Calculator size={20} />
                Enter your recovered copper mass
              </div>
            )}
            {!yieldCalculated && currentStep === 3 && (
              <div className="status-message info">
                <Calculator size={20} />
                Click to calculate your percent yield
              </div>
            )}
            {yieldCalculated && (
              <div className="status-message success">
                <Trophy size={20} />
                Copper Cycle Complete! Final yield: {percentYield.toFixed(1)}%
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Understanding Percent Yield</h4>
            <p>
              In the copper cycle, your theoretical yield equals your starting copper mass
              (since no copper atoms are created or destroyed).
            </p>
            <div className="yield-ranges">
              <div className="range excellent">
                <span className="bar"></span>
                <span>90-100%: Excellent technique</span>
              </div>
              <div className="range good">
                <span className="bar"></span>
                <span>85-90%: Good technique</span>
              </div>
              <div className="range acceptable">
                <span className="bar"></span>
                <span>75-85%: Acceptable</span>
              </div>
              <div className="range low">
                <span className="bar"></span>
                <span>&lt;75%: Review transfers</span>
              </div>
            </div>
            <p className="warning">
              <AlertCircle size={14} />
              Yield &gt;100% suggests contamination or incomplete drying
            </p>
          </div>
        </div>

        <style>{`
          .calculation-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 30px;
            padding: 20px;
            width: 100%;
          }

          .data-cards {
            display: flex;
            align-items: center;
            gap: 15px;
          }

          .data-card {
            background: var(--bg-elevated);
            border: 2px solid var(--border);
            border-radius: 12px;
            padding: 16px 24px;
            min-width: 140px;
            text-align: center;
            transition: all 0.3s ease;
          }

          .data-card.active {
            border-color: var(--secondary);
            box-shadow: 0 0 15px rgba(0, 168, 107, 0.3);
          }

          .data-card.clickable {
            cursor: pointer;
          }

          .data-card.clickable:hover {
            transform: translateY(-2px);
          }

          .data-card.calculated {
            border-color: ${copperCycleColors.copperPrecipitate};
          }

          .card-header {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            margin-bottom: 8px;
          }

          .card-label {
            font-size: 12px;
            color: var(--text-secondary);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .check-icon {
            color: var(--secondary);
          }

          .card-value {
            font-size: 20px;
            font-weight: 600;
            margin-bottom: 4px;
          }

          .card-value .value {
            color: var(--text-primary);
            font-family: monospace;
          }

          .card-value .value.yield {
            font-size: 28px;
          }

          .card-value .placeholder {
            color: var(--text-secondary);
            font-size: 14px;
            font-weight: 400;
          }

          .card-desc {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .card-desc.status {
            font-weight: 600;
            margin-top: 6px;
          }

          .arrow-container {
            color: var(--text-secondary);
          }

          .calculation-box {
            background: var(--bg-elevated);
            border-radius: 12px;
            padding: 20px;
            width: 100%;
            max-width: 500px;
          }

          .calculation-box h4 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 0 16px 0;
            color: var(--text-primary);
          }

          .formula {
            background: rgba(0, 0, 0, 0.2);
            padding: 16px;
            border-radius: 8px;
            font-family: monospace;
          }

          .formula-line {
            padding: 6px 0;
            color: var(--text-secondary);
          }

          .formula-line.result {
            color: var(--text-primary);
            font-size: 18px;
            font-weight: 600;
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px solid var(--border);
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 24px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: linear-gradient(90deg, rgba(0, 168, 107, 0.2), rgba(184, 115, 51, 0.2));
            color: ${copperCycleColors.copperPrecipitate};
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .yield-ranges {
            margin: 12px 0;
          }

          .range {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 4px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .range .bar {
            width: 20px;
            height: 8px;
            border-radius: 2px;
          }

          .range.excellent .bar { background: #FFD700; }
          .range.good .bar { background: #4ADE80; }
          .range.acceptable .bar { background: #60A5FA; }
          .range.low .bar { background: #FBBF24; }

          .theory-box p.warning {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #EF4444;
            font-size: 12px;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid var(--border);
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp5"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
